/* eslint-disable no-magic-numbers */
import { useCallback, useEffect, useRef, useState } from 'react';

import { AnalysisResponse } from '@futrli-sdk/predict_analysis';
import { Logger } from '@futrli/shared-services';

import { getAiAnalysis } from '../../core/api/ai-insights';

const POLL_INTERVAL_MS = 60000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

interface UseAiAnalysisResult {
  analysisResponse: AnalysisResponse | null;
  isLoading: boolean;
  isPending: boolean;
  refreshAnalysis: () => Promise<void>;
  hasFailed: boolean;
  hasTimedOut: boolean;
}

export const useAiAnalysis = (workspaceId: string, organisationId: string, isActive = true): UseAiAnalysisResult => {
  const [analysisResponse, setAnalysisResponse] = useState<AnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const [polling, setPolling] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const requestIdRef = useRef(0);
  const hasTimedOutRef = useRef(false);

  const fetchAnalysis = useCallback(async () => {
    try {
      requestIdRef.current += 1;
      const thisRequestId = requestIdRef.current;
      const response = await getAiAnalysis(workspaceId, organisationId);

      if (thisRequestId !== requestIdRef.current) {
        return;
      }

      setAnalysisResponse(response);
      setIsLoading(false);

      // completed or failed → stop polling
      if (response.analysis.status === 'completed' || response.analysis.status === 'failed') {
        setPolling(false);
        if (response.analysis.status === 'failed') {
          setHasFailed(true);
        }
        return;
      }

      // pending → start polling
      if (!hasTimedOutRef.current) {
        setPolling(true);
      }
    } catch (error: unknown) {
      Logger.error('Error fetching AI analysis');
      setIsLoading(false);
      if (!hasTimedOutRef.current) {
        setPolling(true);
      }
    }
  }, [workspaceId, organisationId]);

  // Initial fetch
  useEffect(() => {
    if (isActive) {
      hasTimedOutRef.current = false;
      setHasTimedOut(false);
      fetchAnalysis();
    }
  }, [fetchAnalysis, isActive]);

  // Polling
  useEffect(() => {
    if (!polling || !isActive) {
      return;
    }

    const id = setInterval(() => {
      fetchAnalysis();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(id);
    };
  }, [fetchAnalysis, polling, isActive]);

  // Timeout — stop polling after 30 minutes
  useEffect(() => {
    if (!polling || !isActive) {
      return;
    }

    const id = setTimeout(() => {
      hasTimedOutRef.current = true;
      setPolling(false);
      setHasTimedOut(true);
    }, POLL_TIMEOUT_MS);

    return () => {
      clearTimeout(id);
    };
  }, [polling, isActive]);

  const isPending = !hasFailed && !hasTimedOut && (!analysisResponse || analysisResponse.analysis.status !== 'completed');

  return { analysisResponse, isLoading, isPending, hasFailed, hasTimedOut, refreshAnalysis: fetchAnalysis };
};
